export type Service = {
  slug: 'logo-design' | 'website-design' | 'website-development';
  title: string;
  tagline: string;
  overview: string[];
  included: string[];
  process: { step: string; title: string; description: string }[];
  deliverables: string[];
  timeline: { label: string; value: string }[];
  clientInputs: string[];
  notIncluded: string[];
  faq: { question: string; answer: string }[];
  illustration: 'branding' | 'designLayout' | 'codeLaptop';
};

export const services: Service[] = [
  {
    slug: 'logo-design',
    title: 'Logo design',
    tagline: 'A mark that does the work for years - not a decoration.',
    overview: [
      'A logo is the compression of a brand into a shape that still reads at sixteen pixels. We build it by understanding what you actually do, who it is for, and why someone would pick you over the obvious choice next door.',
      'No generic shields, no floating swooshes, no tortured abbreviations. Each direction we present has a reason behind it - and an argument for why the other directions would be wrong.',
    ],
    included: [
      'Discovery call and written brief',
      'Competitive landscape audit',
      'Three distinct visual directions',
      'Up to four rounds of revisions on the chosen direction',
      'Primary and secondary lockups',
      'Responsive logo system for small and large sizes',
      'Color palette with hex, RGB, CMYK values',
      'Typography pairing for supporting copy',
      'Delivery in SVG, PNG, PDF, and AI',
    ],
    process: [
      { step: '01', title: 'Listen', description: 'One hour call. You talk, we write things down. We leave with a one-page brief you sign off.' },
      { step: '02', title: 'Map', description: 'We study your direct competition and the cliches we want to avoid. You get a short audit document.' },
      { step: '03', title: 'Shape', description: 'Three directions with a written rationale for each. You tell us which one is closest to the truth.' },
      { step: '04', title: 'Refine', description: 'Up to four revisions on the chosen direction. We stop when you would not change anything else.' },
      { step: '05', title: 'Ship', description: 'Final files in every format. A short usage guide so your team does not butcher it later.' },
    ],
    deliverables: [
      'Master SVG source file',
      'PNG exports at 512, 1024, 2048 px',
      'Print-ready PDF',
      'Adobe Illustrator (.ai) original',
      'One-page usage guide (do / do not)',
      'Favicon in SVG + 32px PNG',
    ],
    timeline: [
      { label: 'Brief and discovery', value: '1-3 days' },
      { label: 'First three directions', value: '5-7 days' },
      { label: 'Revisions on chosen direction', value: '5-10 days' },
      { label: 'Final delivery', value: '2-3 weeks total' },
    ],
    clientInputs: [
      'A written description of what your business does',
      'List of direct competitors (or brands you admire)',
      'Existing visual assets: previous logos, color preferences, fonts you like',
      'Primary usage context (web, print, signage, packaging)',
      'Who signs off on the direction (one person is best)',
    ],
    notIncluded: [
      'Stock photography or illustration',
      'Copywriting for marketing materials',
      'Physical stationery printing (we deliver print-ready files)',
      'Ongoing retainer for new assets',
    ],
    faq: [
      { question: 'How long does a logo project take?', answer: 'Two to three weeks from brief to final files. The pace depends on how quickly you can react to drafts, not on how long we take to draw.' },
      { question: 'Do I own the logo?', answer: 'Yes. Full copyright transfers to you on final payment. We ask for permission to show it in our future portfolio - nothing more.' },
      { question: 'What if I do not like any of the three directions?', answer: 'It is included in the price - we produce one more round of three directions. If the brief is wrong, we rewrite it together before drawing anything else.' },
      { question: 'Can you design the full brand identity around the logo?', answer: 'Yes. That is the Identity tier, which covers stationery, social templates, and a proper brand guidelines document.' },
    ],
    illustration: 'branding',
  },
  {
    slug: 'website-design',
    title: 'Website design',
    tagline: 'Interfaces built page by page - not dragged out of a kit.',
    overview: [
      'Most agency websites look the same because they are built from the same five templates. We start from your content and your audience, not from a theme marketplace.',
      'Every screen is drawn in Figma as a real responsive composition - desktop, tablet, mobile - with proper states, edge cases, and loading behaviour. Developers get a design that can actually be built.',
    ],
    included: [
      'Sitemap and content audit',
      'Wireframes for every unique page',
      'Visual design for desktop (1440 px) and mobile (375 px)',
      'Design system: typography, color, spacing, components',
      'Interactive prototype in Figma for user-flow review',
      'Hover, focus, loading, empty, and error states',
      'Accessibility contrast check',
      'Developer handoff file with exported assets',
    ],
    process: [
      { step: '01', title: 'Brief', description: 'We agree on goals, audiences, and success metrics in writing before anything is drawn.' },
      { step: '02', title: 'Sitemap', description: 'The full map of pages and the shortest path through them. You approve before we go visual.' },
      { step: '03', title: 'Wireframe', description: 'Grayscale layouts for every unique page. Content is real, not placeholder.' },
      { step: '04', title: 'Visual', description: 'High-fidelity design in Figma. Two revision rounds are standard.' },
      { step: '05', title: 'Prototype', description: 'Clickable review of flows. You tap through it on your phone and write notes.' },
      { step: '06', title: 'Handoff', description: 'Developer-ready file with specs, exports, and a short written guide.' },
    ],
    deliverables: [
      'Figma source file with organized layers',
      'Design system library (colors, fonts, components)',
      'Responsive comps: 375, 768, 1440 px',
      'Interactive prototype link',
      'Icon and illustration selection',
      'Exported hero imagery (if applicable)',
      'Handoff document (specs + do / do not notes)',
    ],
    timeline: [
      { label: 'Sitemap and brief', value: '3-5 days' },
      { label: 'Wireframes', value: '1-2 weeks' },
      { label: 'Visual design', value: '2-3 weeks' },
      { label: 'Handoff', value: '4-7 weeks total' },
    ],
    clientInputs: [
      'Final or near-final copy for every page',
      'Brand assets: logo, colors, fonts (or combine this with Logo Design)',
      'Any photography or imagery to include',
      'Sitemap or list of pages you want',
      'Reference sites: what works, what does not',
    ],
    notIncluded: [
      'Copywriting or content strategy',
      'Custom photography or video',
      'Stock asset licensing fees',
      'Frontend or backend development (see Website Development)',
    ],
    faq: [
      { question: 'Do you use templates?', answer: 'No. Every layout is drawn for the project. We may reuse patterns we have tested before, but nothing is copy-pasted.' },
      { question: 'How many pages are included?', answer: 'It depends on tier. Landing covers one page, Starter covers up to five unique page templates, Business covers up to ten.' },
      { question: 'Can you design in our existing brand style?', answer: 'Yes. If you have a brand book, we work inside it. If you do not, we can combine this project with our Logo Design service.' },
      { question: 'Do you write the copy too?', answer: 'We do not, but we will flag any section where the copy is clearly hurting the design and help you rewrite it.' },
    ],
    illustration: 'designLayout',
  },
  {
    slug: 'website-development',
    title: 'Website development',
    tagline: 'Hand-built React sites that load fast and age well.',
    overview: [
      'We build marketing sites and small web applications in React and TypeScript. No page builders, no block themes, no bloated frameworks you will be stuck maintaining next year.',
      'The output is clean code, real accessibility, fast load times, and a deployment a junior developer can read. If you ever want to leave us, you can - the repo is yours and readable.',
    ],
    included: [
      'React + Vite + TypeScript setup',
      'Tailwind-based design system implementation',
      'Responsive layout for 375 to 1920 px',
      'Accessible interactive components',
      'Contact form with validation',
      'SEO meta tags and semantic HTML',
      'Lighthouse score 95+ on Performance and Accessibility',
      'Production build and deployment instructions',
    ],
    process: [
      { step: '01', title: 'Setup', description: 'Project scaffolding, design tokens, and the first deploy preview - within two days of start.' },
      { step: '02', title: 'Foundation', description: 'Layout, navigation, shared components. Everything the rest of the build will stand on.' },
      { step: '03', title: 'Pages', description: 'Page by page, with live preview links. You click, we read your notes, we iterate.' },
      { step: '04', title: 'Polish', description: 'Accessibility audit, performance pass, cross-browser check, animation tuning.' },
      { step: '05', title: 'Launch', description: 'Production build, deployment to your host, and a handover call for your team.' },
    ],
    deliverables: [
      'Git repository with clean commit history',
      'Production build (static files)',
      'Deployment walkthrough document',
      'README with install, dev, build commands',
      'Lighthouse audit report',
      'One month of defect fixes included',
    ],
    timeline: [
      { label: 'Setup and foundation', value: '3-5 days' },
      { label: 'Page build', value: '2-4 weeks' },
      { label: 'Polish and launch', value: '5-7 days' },
      { label: 'Five-page site', value: '4-6 weeks total' },
    ],
    clientInputs: [
      'Approved design file in Figma with all states and breakpoints',
      'Final copy in editable format (Google Doc, Notion)',
      'Hosting preference (we deploy to Vercel, Netlify, or your server)',
      'Domain DNS access at launch',
      'Decision on CMS vs hard-coded content',
    ],
    notIncluded: [
      'Design work (see Website Design)',
      'Content entry for more than ten pages',
      'Third-party service subscriptions (CMS, analytics, email)',
      'Ongoing maintenance beyond the included support period',
    ],
    faq: [
      { question: 'What happens after launch?', answer: 'The Landing tier includes one month of defect fixes, Business includes three months. After that we can keep a monthly retainer or hand everything to your internal team.' },
      { question: 'Do you offer a CMS?', answer: 'Optional. We integrate headless CMS like Sanity or Payload when content really needs to be editable by non-developers. For most marketing sites, plain code is lighter and simpler.' },
      { question: 'Can you take over an existing project?', answer: 'Sometimes. We audit the codebase first and tell you honestly whether it is worth maintaining or worth rebuilding.' },
      { question: 'Where is the site hosted?', answer: 'Your choice. We output a static build that runs anywhere - Vercel, Netlify, Cloudflare Pages, your own server. We document the process.' },
    ],
    illustration: 'codeLaptop',
  },
];

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}
